// src/components/Business/Scheduling/PersonalCalendarSection.jsx
// W1 — personal calendar sync. ADDITIVE: it renders as one more <Section> inside the EXISTING
// Scheduling settings surface. No new route, no new page, no second integrations dashboard.
//
// THE SERVER DECIDES EVERYTHING THIS RENDERS:
//   * which providers exist and whether each is usable -> GET /scheduling/calendar/providers
//   * whether this person is connected, and how healthy -> GET /scheduling/calendar/connection
//   * whether connecting is even possible right now     -> the same providers payload
//
// It is deliberately NOT a "Connect Google" button. The surface models "choose a calendar provider"
// with Google as the one currently available, so adding Microsoft later is a server change and a
// label — not a redesign. A provider the server reports as unavailable renders as explicitly
// unavailable rather than being hidden, because hiding it would misrepresent the roadmap and
// showing it as working would misrepresent the product.
//
// NOTHING SECRET REACHES THIS FILE. The API returns no token, no refresh token and no connector
// instance id, so there is no field here that could leak one. The account is shown only as the
// masked label the server chose.

import React, { useCallback, useEffect, useState } from 'react';
import { Button } from '../../ui/button';
import { CalendarClock, Loader2, AlertTriangle, CheckCircle2 } from 'lucide-react';

const BASE = process.env.REACT_APP_API_BASE || process.env.REACT_APP_BACKEND_URL || '';

// Human labels for the governed provider discriminators. A label table is not an authority: a
// provider absent from the server payload is never rendered, whatever this map says.
const PROVIDER_LABELS = {
  google_calendar: 'Google Calendar',
  microsoft_calendar: 'Microsoft Outlook',
  caldav: 'Other (CalDAV)',
};

const STATUS_TONE = {
  active: 'text-green-700',
  needs_attention: 'text-amber-700',
  disconnected: 'text-gray-500',
  pending: 'text-gray-500',
};

export default function PersonalCalendarSection({ brandId, getToken, tenantHeaders, t }) {
  const [providers, setProviders] = useState([]);
  const [connection, setConnection] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null);
  const [error, setError] = useState(null);
  const [notAvailable, setNotAvailable] = useState(false);

  const authHeaders = useCallback(async () => {
    const token = await getToken();
    return { ...(tenantHeaders ? tenantHeaders() : {}), Authorization: `Bearer ${token}` };
  }, [getToken, tenantHeaders]);

  const load = useCallback(async () => {
    if (!brandId) return;
    setLoading(true);
    setError(null);
    try {
      const headers = await authHeaders();
      const q = `business_id=${encodeURIComponent(brandId)}`;
      const [pr, cn] = await Promise.all([
        fetch(`${BASE}/api/v1/scheduling/calendar/providers?${q}`, { headers }),
        fetch(`${BASE}/api/v1/scheduling/calendar/connection?${q}`, { headers }),
      ]);
      // 403 here means the capability is not entitled for this tenant, or this role may not manage
      // a calendar. Both are legitimate states, not errors — the section simply does not apply.
      if (pr.status === 403 || cn.status === 403) { setNotAvailable(true); return; }
      if (!pr.ok) throw new Error(`HTTP ${pr.status}`);
      const pdata = await pr.json();
      setProviders(pdata.providers || []);
      setConnection(cn.ok ? await cn.json() : null);
    } catch (e) {
      setError(e.message || 'error');
    } finally {
      setLoading(false);
    }
  }, [brandId, authHeaders]);

  useEffect(() => { load(); }, [load]);

  const connect = useCallback(async (providerType) => {
    setBusy(`connect:${providerType}`);
    setError(null);
    try {
      const headers = { ...(await authHeaders()), 'Content-Type': 'application/json' };
      const resp = await fetch(`${BASE}/api/v1/scheduling/calendar/connect`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ business_id: brandId, provider_type: providerType }),
      });
      const data = await resp.json().catch(() => ({}));
      if (!resp.ok || !data.authorization_url) {
        throw new Error((data.detail && data.detail.reason) || `HTTP ${resp.status}`);
      }
      // The consent screen is the provider's own page. Opening it in a new tab keeps the
      // Scheduling settings state intact behind it; the callback lands server-side, so this tab
      // only has to reload when the person comes back.
      window.open(data.authorization_url, '_blank', 'noopener');
    } catch (e) {
      setError(e.message || 'error');
    } finally {
      setBusy(null);
    }
  }, [brandId, authHeaders]);

  const disconnect = useCallback(async (providerType) => {
    setBusy(`disconnect:${providerType}`);
    setError(null);
    try {
      const headers = { ...(await authHeaders()), 'Content-Type': 'application/json' };
      const resp = await fetch(`${BASE}/api/v1/scheduling/calendar/disconnect`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ business_id: brandId, provider_type: providerType }),
      });
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
      await load();
    } catch (e) {
      setError(e.message || 'error');
    } finally {
      setBusy(null);
    }
  }, [brandId, authHeaders, load]);

  if (notAvailable) return null;

  // ONE translation authority: the shared catalogue via the `t` this surface is already given.
  // The English string stays as the resilience fallback the language layer itself uses, so a
  // caller that renders this section without `t` behaves exactly as before.
  const tr = (key, en, params) => (t ? t(key, params) : en);

  const connected = connection && connection.connected;
  const status = (connection && connection.status) || 'disconnected';
  const health = (connection && connection.sync_health) || 'never_synced';

  return (
    <div data-testid="personal-calendar">
      {loading && (
        <div className="flex items-center gap-2 text-sm text-gray-500" data-testid="calendar-loading">
          <Loader2 className="h-4 w-4 animate-spin" /> {tr('schedcfg.calendar.loading', 'Loading…')}
        </div>
      )}

      {error && (
        <div className="text-sm text-red-600 mb-2" data-testid="calendar-error">{error}</div>
      )}

      {!loading && (
        <>
          <div className="flex items-center gap-2 mb-2">
            <CalendarClock className="h-4 w-4 text-gray-500" />
            <span className="text-sm text-gray-700">
              {tr('schedcfg.calendar.intro',
                  'Connect your own calendar so your outside commitments block your bookable times.')}
            </span>
          </div>

          <div className="text-sm mb-3" data-testid="calendar-status">
            <span className="text-gray-500">{tr('schedcfg.calendar.statusLabel', 'Status:')} </span>
            <span className={STATUS_TONE[status] || 'text-gray-600'} data-testid="calendar-status-value">
              {connected
                ? tr('schedcfg.calendar.statusConnected', 'Connected')
                : status === 'needs_attention'
                  ? tr('schedcfg.calendar.statusAttention', 'Needs attention')
                  : tr('schedcfg.calendar.statusNotConnected', 'Not connected')}
            </span>
            {connected && (
              <>
                {connection.external_account_label && (
                  <span className="text-gray-500" data-testid="calendar-account">
                    {' · '}{connection.external_account_label}
                  </span>
                )}
                <span className="text-gray-500" data-testid="calendar-health">
                  {' · '}{tr('schedcfg.calendar.syncLabel', 'Sync:')}{' '}
                  {health === 'ok' ? tr('schedcfg.calendar.syncOk', 'up to date')
                    : health === 'stale' ? tr('schedcfg.calendar.syncStale', 'out of date')
                    : health === 'error' ? tr('schedcfg.calendar.syncError', 'needs attention')
                      : tr('schedcfg.calendar.syncNever', 'not synced yet')}
                </span>
                {connection.last_synced_at && (
                  <span className="text-gray-400" data-testid="calendar-last-sync">
                    {' · '}{tr('schedcfg.calendar.lastSync', `Last sync ${connection.last_synced_at}`,
                                 { ts: connection.last_synced_at })}
                  </span>
                )}
              </>
            )}
          </div>

          {status === 'needs_attention' && (
            <div className="text-xs text-amber-700 flex items-center gap-1 mb-2" data-testid="calendar-attention">
              <AlertTriangle className="h-3.5 w-3.5" />
              {tr('schedcfg.calendar.attentionBanner',
                  'Your calendar stopped syncing. Reconnect to keep your availability accurate.')}
            </div>
          )}

          <div className="flex flex-wrap gap-2" data-testid="calendar-providers">
            {providers.map((p) => {
              // Brand names are not translated; the one non-brand entry is.
              const label = p.provider_type === 'caldav'
                ? tr('schedcfg.calendar.providerOther', PROVIDER_LABELS.caldav || 'Other (CalDAV)')
                : (PROVIDER_LABELS[p.provider_type] || p.provider_type);
              const isCurrent = connected && connection.provider_type === p.provider_type;
              if (isCurrent) {
                return (
                  <Button
                    key={p.provider_type}
                    size="sm"
                    variant="outline"
                    disabled={busy === `disconnect:${p.provider_type}`}
                    onClick={() => disconnect(p.provider_type)}
                    data-testid={`calendar-disconnect-${p.provider_type}`}
                  >
                    <CheckCircle2 className="h-3.5 w-3.5 mr-1 text-green-600" />
                    {tr('schedcfg.calendar.disconnect', `Disconnect ${label}`, { provider: label })}
                  </Button>
                );
              }
              return (
                <Button
                  key={p.provider_type}
                  size="sm"
                  variant="outline"
                  // A provider with no adapter, or one whose platform credentials are absent, is
                  // shown as unavailable rather than hidden or faked.
                  disabled={!p.available || busy === `connect:${p.provider_type}`}
                  onClick={() => connect(p.provider_type)}
                  data-testid={`calendar-connect-${p.provider_type}`}
                >
                  {busy === `connect:${p.provider_type}`
                    ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" />
                    : <CalendarClock className="h-3.5 w-3.5 mr-1" />}
                  {p.available
                    ? (status === 'needs_attention' && connection
                       && connection.provider_type === p.provider_type
                        ? tr('schedcfg.calendar.reconnect', `Reconnect ${label}`, { provider: label })
                        : tr('schedcfg.calendar.connect', `Connect ${label}`, { provider: label }))
                    : tr('schedcfg.calendar.unavailable', `${label} — not available yet`,
                         { provider: label })}
                </Button>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
}
